import { getAuthHeaders } from '../utils/authHeaders';
import { createFormPdf, PDF_COLORS, yesNoColor } from '../utils/formPdf';

const API_URL = 'https://app.pvslimited.co.uk/api';

const sendVesselFailureReportEmail = async (formData) => {
  try {
    const isHighRisk = formData.immediateRiskLevel === 'High';
    const blob = await createFormPdf({
      title: 'VESSEL FAILURE REPORT',
      ref: 'Ref: PVS-VFR-001 | Version: 1.0',
      subtitle: isHighRisk ? 'HIGH RISK - EMERGENCY PROTOCOL ACTIVATED' : `Status: ${formData.status || 'N/A'}`,
      titleColor: isHighRisk ? PDF_COLORS.red : PDF_COLORS.navy,
      subtitleColor: isHighRisk ? PDF_COLORS.red : PDF_COLORS.muted,
    }, (pdf) => {
      if (isHighRisk) {
        pdf.banner('HIGH RISK - EMERGENCY PROTOCOL ACTIVATED', PDF_COLORS.red);
      } else {
        pdf.field('Status', formData.status);
      }

      pdf.section('1. REPORT INFORMATION');
      pdf.field('Date Logged', formData.dateLogged);
      pdf.field('Logged By', formData.loggedBy);
      pdf.field('Department/Site Location', formData.departmentLocation);
      pdf.field('Status', formData.status);

      pdf.section('2. VESSEL IDENTIFICATION');
      pdf.field('Vessel Serial Number', formData.vesselSerialNumber);
      pdf.field('Manufacturer', formData.manufacturer);
      pdf.field('Model/Vessel Type', formData.modelVesselType);
      pdf.field('Vessel Water Capacity (Litres)', formData.vesselWaterCapacity);
      pdf.field('Year of Manufacture', formData.yearOfManufacture);

      pdf.section('3. FAILURE EVENT SUMMARY');
      pdf.field('Failure Categories', formData.failureCategories?.join(', '));
      pdf.field('Reason for Failure', formData.reasonForFailure);
      pdf.field('Operational State', formData.operationalState);

      pdf.section('4. SAFETY & IMPACT ASSESSMENT');
      pdf.field('Immediate Risk Level', formData.immediateRiskLevel, yesNoColor(formData.immediateRiskLevel));
      pdf.field(
        'Any Injuries/Near Misses',
        formData.anyInjuries === 'yes' ? `Yes - ${formData.injuryDetails}` : 'No'
      );
      pdf.field(
        'Impact on Operations',
        `${formData.impactOnOps}${formData.impactDetails ? ' - ' + formData.impactDetails : ''}`
      );

      pdf.section('5. EVIDENCE PACKAGE');
      pdf.subsection('Inspection Readings');
      if (formData.inspectionReadings?.length > 0) {
        formData.inspectionReadings.forEach((r) => {
          pdf.field(r.date, `${r.sensor} = ${r.value} ${r.units} (${r.operator})`);
        });
      } else {
        pdf.paragraph('No readings recorded', { size: 10, color: PDF_COLORS.muted });
      }
      pdf.subsection('Witness Statements');
      if (formData.witnessStatements?.length > 0) {
        formData.witnessStatements.forEach((w) => {
          pdf.field(`${w.name} (${w.role})`, w.statement);
        });
      } else {
        pdf.paragraph('No witness statements recorded', { size: 10, color: PDF_COLORS.muted });
      }

      pdf.section('6. INVESTIGATION & ACTIONS');
      pdf.field('Suspected Root Cause', formData.suspectedRootCause);
      pdf.field('Root Cause Details', formData.rootCauseDetails);
      pdf.check('Can Failure Be Rectified', formData.canFailureBeRectified);
      if (formData.canFailureBeRectified === 'no') {
        pdf.field(
          'Vessel To Be Scrapped',
          formData.vesselToBeScrapped === 'yes' ? 'YES - VESSEL MARKED FOR SCRAPPING' : 'No',
          formData.vesselToBeScrapped === 'yes' ? PDF_COLORS.red : PDF_COLORS.dark
        );
      }

      pdf.section('7. CLOSE OUT');
      pdf.check('Corrective Action Verified', formData.correctiveActionVerified);
      pdf.field('Updated in Vessel Inventory', formData.updatedInVesselInventory ? 'Yes' : 'No');
      pdf.field('QC Manager Sign-off', formData.qcManagerSignOff);
      pdf.field('Sign-off Date', formData.qcManagerSignOffDate);
    });

    const formDataToSend = new FormData();
    formDataToSend.append('file', blob, `Vessel_Failure_Report_${formData.vesselSerialNumber}_${Date.now()}.pdf`);
    formDataToSend.append('vesselSerialNumber', formData.vesselSerialNumber);
    formDataToSend.append('loggedBy', formData.loggedBy);
    formDataToSend.append('immediateRiskLevel', formData.immediateRiskLevel);
    formDataToSend.append('failureCategories', formData.failureCategories.join(', '));
    formDataToSend.append('formType', 'vessel-failure-report');

    if (formData.attachments && formData.attachments.length > 0) {
      formData.attachments.forEach((attachment) => {
        formDataToSend.append('attachments', attachment.file, attachment.name);
      });
    }

    const response = await fetch(`${API_URL}/send-vessel-failure-report-email`, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: formDataToSend,
    });

    if (!response.ok) {
      const errorText = await response.text();
      throw new Error(`HTTP error! status: ${response.status}, message: ${errorText}`);
    }

    return response.json();
  } catch (error) {
    console.error('Error in sendVesselFailureReportEmail:', error);
    throw error;
  }
};

export default sendVesselFailureReportEmail;
